import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { appStorage } from '@sternum/web/services/appStorage';

import { SearchFilterByTextPerCategory } from '../models';

import { SetSearchFiltersByInputForGivenCategory, UpdateSearchAnomaliesValue } from './anomalies.types';

export interface AnomaliesState {
  isRowExpandedPerId: Record<string, boolean>;
  isFiltersSidebarOpen: boolean;
  showAnomaliesOvertimeGraph: boolean;
  searchAnomaliesValue: string;
  searchFiltersByTextPerCategory: SearchFilterByTextPerCategory;
  areAnomalyNotesVisible: boolean;
  areAnomalyLogsBeforeExpanded: boolean;
  areAnomalyLogsAfterExpanded: boolean;
  isAnomalyLogsViewInContextTouched: boolean;
  resetAnomalyLogsId: number;
}

const getInitialState: () => AnomaliesState = () => ({
  isRowExpandedPerId: {},
  isFiltersSidebarOpen: appStorage.getIsAnomaliesFiltersSidebarOpen() ?? true,
  searchAnomaliesValue: '',
  searchFiltersByTextPerCategory: {},
  showAnomaliesOvertimeGraph: appStorage.getIsAnomaliesGraphVisible() ?? true,
  areAnomalyNotesVisible: appStorage.getAreAnomalyNotesVisible() ?? false,
  areAnomalyLogsBeforeExpanded: true,
  areAnomalyLogsAfterExpanded: false,
  isAnomalyLogsViewInContextTouched: false,
  resetAnomalyLogsId: 1,
});

export const anomaliesSlice = createSlice({
  name: 'anomalies',
  initialState: getInitialState,
  reducers: {
    toggleIsFilterSidebarOpenAction(state) {
      state.isFiltersSidebarOpen = !state.isFiltersSidebarOpen;
    },

    setSearchFiltersByInputForGivenCategory(state, action: PayloadAction<SetSearchFiltersByInputForGivenCategory>) {
      const { categoryKey, value } = action.payload;

      if (value) {
        state.searchFiltersByTextPerCategory[categoryKey] = value;
      } else {
        delete state.searchFiltersByTextPerCategory[categoryKey];
      }
    },

    toggleRowAction: function (state, action: PayloadAction<string>) {
      const rowId = action.payload;
      const isRowExpanded = state.isRowExpandedPerId[rowId];

      if (isRowExpanded) {
        delete state.isRowExpandedPerId[rowId];
      } else {
        state.isRowExpandedPerId[action.payload] = true;
      }
    },

    collapseAllAction(state) {
      state.isRowExpandedPerId = {};
    },

    updateSearchAnomaliesValue(state, action: PayloadAction<UpdateSearchAnomaliesValue>) {
      // In case of navigating back, there is no need to change the state or to stop polling
      if (action.payload.value !== state.searchAnomaliesValue) {
        state.searchAnomaliesValue = action.payload.value?.trim();
      }
    },

    clearAnomaliesState() {
      return getInitialState();
    },

    toggleAreAnomalyLogsBeforeExpanded(state, action: PayloadAction<boolean | undefined>) {
      state.areAnomalyLogsBeforeExpanded =
        action.payload === undefined ? !state.areAnomalyLogsBeforeExpanded : action.payload;
    },

    toggleAreAnomalyLogsAfterExpanded(state, action: PayloadAction<boolean | undefined>) {
      state.areAnomalyLogsAfterExpanded =
        action.payload === undefined ? !state.areAnomalyLogsAfterExpanded : action.payload;
    },

    toggleIsAnomalyLogsViewInContextTouched(state, action: PayloadAction<boolean | undefined>) {
      state.isAnomalyLogsViewInContextTouched =
        action.payload === undefined ? !state.isAnomalyLogsViewInContextTouched : action.payload;
    },

    toggleShowAnomaliesOvertimeGraph(state) {
      state.showAnomaliesOvertimeGraph = !state.showAnomaliesOvertimeGraph;
      appStorage.setIsAnomaliesGraphVisible(state.showAnomaliesOvertimeGraph);
    },

    toggleAreAnomalyNotesVisibleAction(state) {
      state.areAnomalyNotesVisible = !state.areAnomalyNotesVisible;
    },

    resetAreAnomalyLogsBeforeAfterExpanded(state) {
      const initialState = getInitialState();

      state.areAnomalyLogsBeforeExpanded = initialState.areAnomalyLogsBeforeExpanded;
      state.areAnomalyLogsAfterExpanded = initialState.areAnomalyLogsAfterExpanded;
    },

    triggerResetAnomalyLogIdAction(state) {
      state.resetAnomalyLogsId = (state.resetAnomalyLogsId % 1000) + 1; // value from 1 to 1000
    },
  },
});

export const {
  clearAnomaliesState,
  collapseAllAction,
  updateSearchAnomaliesValue,
  setSearchFiltersByInputForGivenCategory,
  toggleIsFilterSidebarOpenAction,
  toggleShowAnomaliesOvertimeGraph,
  toggleRowAction,
  toggleAreAnomalyLogsBeforeExpanded,
  toggleAreAnomalyLogsAfterExpanded,
  toggleIsAnomalyLogsViewInContextTouched,
  toggleAreAnomalyNotesVisibleAction,
  resetAreAnomalyLogsBeforeAfterExpanded,
  triggerResetAnomalyLogIdAction,
} = anomaliesSlice.actions;
